import React, { useEffect, useMemo, useState } from 'react';
import { useData } from '../../hooks/useData';
import { MetricStrip } from '../common/MetricStrip';
import { EmptyState } from '../common/EmptyState';
import { Tag } from '../common/Tag';
import { fmtDate, relTime } from '../../utils/helpers';
import { adminDashboardServices } from '../../APIServices/adminDashboardServices';
import { useToast } from '../../hooks/useToast';

function RiskTrendChart({ audit = [] }) {
  const points = useMemo(() => {
    const days = 30;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const list = [];

    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);

      const nextDay = new Date(d);
      nextDay.setDate(nextDay.getDate() + 1);

      const count = audit.filter((a) => {
        const at = new Date(a.at || a.createdAt || a.updatedAt);
        return at >= d && at < nextDay;
      }).length;

      list.push({
        date: d,
        count,
      });
    }

    return list;
  }, [audit]);

  const maxCount = Math.max(
    1,
    ...points.map((p) => p.count)
  );

  const w = 720;
  const h = 130;
  const pad = 28;

  const xStep =
    (w - pad * 2) / (points.length - 1);

  const linePath = points
    .map(
      (p, i) =>
        `${i === 0 ? 'M' : 'L'} ${pad + i * xStep
        } ${h -
        pad -
        (p.count / maxCount) *
        (h - pad * 2)
        }`
    )
    .join(' ');

  const fillPath =
    linePath +
    ` L ${pad + (points.length - 1) * xStep
    } ${h - pad} L ${pad} ${h - pad} Z`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      style={{
        width: '100%',
        height: 130,
      }}
    >
      <defs>
        <linearGradient
          id="trendGrad"
          x1="0"
          y1="0"
          x2="0"
          y2="1"
        >
          <stop
            offset="0"
            stopColor="var(--accent)"
            stopOpacity="0.15"
          />
          <stop
            offset="1"
            stopColor="var(--accent)"
            stopOpacity="0"
          />
        </linearGradient>
      </defs>

      {[0.25, 0.5, 0.75].map((p, i) => (
        <line
          key={i}
          x1={pad}
          x2={w - pad}
          y1={
            h -
            pad -
            p * (h - pad * 2)
          }
          y2={
            h -
            pad -
            p * (h - pad * 2)
          }
          stroke="var(--line)"
          strokeDasharray="2,3"
        />
      ))}

      <path
        d={fillPath}
        fill="url(#trendGrad)"
      />

      <path
        d={linePath}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="1.5"
      />

      {points.map((p, i) => (
        <circle
          key={i}
          cx={pad + i * xStep}
          cy={
            h -
            pad -
            (p.count / maxCount) *
            (h - pad * 2)
          }
          r="2.5"
          fill="var(--surface)"
          stroke="var(--accent)"
          strokeWidth="1.5"
        />
      ))}

      <text
        x={pad}
        y={h - 10}
        fontFamily="JetBrains Mono"
        fontSize="9"
        fill="var(--ink-3)"
        style={{
          textTransform: 'uppercase',
        }}
      >
        {points[0]?.date.toLocaleDateString(
          'en',
          {
            day: '2-digit',
            month: 'short',
          }
        )}
      </text>

      <text
        x={w - pad}
        y={h - 10}
        fontFamily="JetBrains Mono"
        fontSize="9"
        fill="var(--ink-3)"
        textAnchor="end"
      >
        Today
      </text>
    </svg>
  );
}

function DashboardModule({ onNavigate }) {
  const { currentUserData } = useData();
  const toast = useToast();

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    activeUsers: 0,
    activeSubAdmins: 0,
    totalDesignations: 0,
    folderNodes: 0,
    documentTypes: 0,
    documentTags: 0,
    projectMilestones: 0,
    reviewChecklistPoints: 0,
    knowledgeMetadata: 0,
    auditCount: 0,
  });
  const [auditRows, setAuditRows] = useState([]);

  const syncDashboardData = async () => {
    setLoading(true);
    try {
      const response = await adminDashboardServices.getAdminDashboardSummary();
      const payload = response?.data || {};
      const summary = payload?.adminSummary || {};

      setStats({
        activeUsers: summary?.activeUsers || 0,
        activeSubAdmins: summary?.activeSubAdmins || 0,
        totalDesignations: summary?.totalDesignations || 0,
        folderNodes: summary?.folderNodes || 0,
        documentTypes: summary?.documentTypes || 0,
        documentTags: summary?.documentTags || 0,
        projectMilestones: summary?.projectMilestones || 0,
        reviewChecklistPoints: summary?.reviewChecklistPoints || 0,
        knowledgeMetadata: summary?.knowledgeMetadata || 0,
        auditCount: summary?.auditCount || 0,
      });

      setAuditRows(payload?.recentActivity || []);
    } catch (error) {
      toast('Unable to load dashboard stats.', 'warn');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    syncDashboardData();
  }, [currentUserData?.userId]);

  const metricItems = useMemo(() => {
    const totalAdmins = stats.activeUsers + stats.activeSubAdmins;
    return [
      {
        label: 'Active accounts',
        value: totalAdmins,
        foot: `${stats.activeUsers} users, ${stats.activeSubAdmins} sub admins`,
        dir: totalAdmins > 0 ? 'ok' : 'warn',
      },
      {
        label: 'Workspace nodes',
        value: stats.folderNodes,
        foot: `${stats.documentTypes} doc types, ${stats.documentTags} doc tags`,
      },
      {
        label: 'Metadata health',
        value: stats.knowledgeMetadata,
        foot: `${stats.totalDesignations} designations configured`,
      },
      {
        label: 'Review checkpoints',
        value: stats.reviewChecklistPoints,
        foot: `${stats.projectMilestones} milestone names`,
      },
      {
        label: 'Audit entries',
        value: stats.auditCount,
        foot: loading ? 'refreshing...' : 'last sync from governance logs',
      },
    ];
  }, [stats, loading]);

  const recentActivity = useMemo(() => {
    const resolveModuleKey = (item) => {
      const category = String(item.category || '').toLowerCase();
      const actionType = String(item.actionType || '').toLowerCase();

      if (category.includes('documenttype') || category.includes('document_type')) return 'documentType';
      if (category.includes('documenttag') || category.includes('document_tag')) return 'documentTag';
      if (category.includes('projectmilestone') || category.includes('project_milestone')) return 'projectMilestone';
      if (category.includes('reviewchecklist') || category.includes('review_checklist')) return 'reviewChecklist';
      if (category.includes('knowledge')) return 'knowledgeBaseMetadata';
      if (category.includes('folder')) return 'studies';
      if (category.includes('designation')) return actionType.includes('subadmin') ? 'subadminDesignation' : 'userDesignation';
      if (category.includes('user')) return actionType.includes('subadmin') ? 'subadmin' : 'user';

      if (actionType.includes('login') || actionType.includes('logout')) return 'logHistory';
      if (actionType.includes('failed')) return 'failedLogHistory';

      return 'audit';
    };

    const rows = (auditRows || [])
      .map((item) => {
        const at = item.createdAt || item.updatedAt || item.at;
        return {
          id: item._id || item.id,
          at,
          actionType: item.actionType || 'activity',
          activityByName: item.activityByName || 'System',
          activities: item.activities || item.updatedValue || item.previousValue || 'Activity updated',
          object: item.category || '-',
          moduleKey: resolveModuleKey(item),
        };
      })
      .filter((item) => item.at)
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .slice(0, 10);

    return rows;
  }, [auditRows]);

  const todayLabel = new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const displayName =
    [currentUserData?.firstName, currentUserData?.lastName].filter(Boolean).join(' ') ||
    currentUserData?.name ||
    'Admin';

  const moduleCoverage = [
    { name: 'Manage User', count: stats.activeUsers, variant: 'ok', moduleKey: 'user' },
    { name: 'Manage Sub Admin', count: stats.activeSubAdmins, variant: 'ok', moduleKey: 'subadmin' },
    { name: 'Document Folder', count: stats.folderNodes, variant: 'default', moduleKey: 'studies' },
    { name: 'Document Type Metadata', count: stats.documentTypes, variant: 'info', moduleKey: 'documentType' },
    { name: 'Document Tag Metadata', count: stats.documentTags, variant: 'info', moduleKey: 'documentTag' },
    { name: 'Project Milestone Metadata', count: stats.projectMilestones, variant: 'warn', moduleKey: 'projectMilestone' },
    { name: 'Review Checklist Metadata', count: stats.reviewChecklistPoints, variant: 'warn', moduleKey: 'reviewChecklist' },
    { name: 'Knowledgebase Metadata', count: stats.knowledgeMetadata, variant: 'danger', moduleKey: 'knowledgeBaseMetadata' },
  ];

  const handleNavigate = (moduleKey) => {
    if (!moduleKey || typeof onNavigate !== 'function') return;
    onNavigate(moduleKey);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Analytics • {todayLabel}</div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-sub">Welcome back, {displayName}. Here is a live summary of admin workspace and governance activity.</p>
        </div>

        <div className="hstack gap-4">
          <button className="btn" type="button" onClick={syncDashboardData} disabled={loading}>
            <i className="ti ti-refresh" /> Refresh
          </button>
        </div>
      </div>

      <MetricStrip items={metricItems} />

      <div className="row mt-4" style={{ alignItems: 'flex-start' }}>
        <div className="col">
          <div className="section-label mb-5">Governance trend • Last 30 days</div>
          <div className="card card-pad">
            <RiskTrendChart audit={auditRows} />
            <div className="xs muted mt-2">
              {(auditRows || []).length} audit events loaded for trend rendering
            </div>
          </div>

          <div className="section-label mt-4 mb-5">Recent activity</div>
          <div className="card">
            {recentActivity.length === 0 ? (
              <EmptyState
                icon="ti-clock"
                title="No recent activity"
                desc="Recent governance and metadata activities will appear here."
              />
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Activity</th>
                    <th>Module</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {recentActivity.map((item) => (
                    <tr
                      key={item.id}
                      className="clickable-row"
                      onClick={() => handleNavigate(item.moduleKey)}
                    >
                      <td className="xs muted">
                        {relTime(item.at)}
                        <div className="xs muted">{fmtDate(item.at)}</div>
                      </td>
                      <td>
                        <div className="medium" style={{ textTransform: 'capitalize' }}>
                          {String(item.actionType).replace(/_/g, ' ')}
                        </div>
                        <div className="xs muted">{item.activities}</div>
                      </td>
                      <td className="xs">{item.object}</td>
                      <td className="xs">
                        <div className="clickable-row-by-cell">
                          <span>{item.activityByName}</span>
                          <i className="ti ti-chevron-right clickable-row-arrow" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div style={{ width: 420, maxWidth: '100%' }}>
          <div className="section-label">Module coverage</div>
          <div className="card card-pad">
            <div className="timeline">
              {moduleCoverage.map((item) => (
                <div
                  className="timeline-item clickable-timeline-item"
                  key={item.name}
                  onClick={() => handleNavigate(item.moduleKey)}
                >
                  <div className="timeline-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{item.name}</span>
                    <div className="hstack gap-2" style={{ alignItems: 'center' }}>
                      <Tag variant={item.variant}>{item.count}</Tag>
                      <i className="ti ti-chevron-right clickable-row-arrow" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="section-label mt-4 mb-5">Quick health checks</div>
          <div className="card card-pad">
            <div className="xs" style={{ marginBottom: 10 }}>
              <Tag variant={stats.auditCount > 0 ? 'ok' : 'warn'} dot>
                {stats.auditCount > 0 ? 'Audit trail active' : 'No audit entries found'}
              </Tag>
            </div>
            <div className="xs" style={{ marginBottom: 10 }}>
              <Tag variant={stats.reviewChecklistPoints > 0 ? 'ok' : 'warn'} dot>
                {stats.reviewChecklistPoints > 0 ? 'Review checklist metadata configured' : 'Review checklist metadata missing'}
              </Tag>
            </div>
            <div className="xs">
              <Tag variant={stats.knowledgeMetadata > 0 ? 'ok' : 'warn'} dot>
                {stats.knowledgeMetadata > 0 ? 'Knowledge metadata ready' : 'Knowledge metadata pending'}
              </Tag>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .clickable-row {
          cursor: pointer;
          transition: background-color 0.2s ease;
        }

        .clickable-row:hover {
          background: var(--surface-2);
        }

        .clickable-row-by-cell {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .clickable-timeline-item {
          cursor: pointer;
          border-radius: 6px;
          transition: transform 0.2s ease, background-color 0.2s ease;
          padding: 6px 8px;
          margin: 0 -8px;
        }

        .clickable-timeline-item:hover {
          transform: translateX(3px);
          background: var(--surface-2);
        }

        .clickable-row-arrow {
          color: var(--ink-3);
          font-size: 14px;
          opacity: 0.75;
        }

        .clickable-row:hover .clickable-row-arrow,
        .clickable-timeline-item:hover .clickable-row-arrow {
          opacity: 1;
          color: var(--ink);
        }
      `}</style>
    </div>
  );
}

export default DashboardModule;